import React, {
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import DeleteVariableTreeNode from '../../../../types/BotBuilder/DeleteVariableTreeNode';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';
import useDebouncedEffect from '../../../../hooks/useDebouncedEffect';

export type DeleteVariableNodeData = {
  node: BotResponseTreeNode;
  isLocked?: boolean;
};

const isDeleteVariableTreeNode = (
  content: any
): content is DeleteVariableTreeNode => {
  return content instanceof DeleteVariableTreeNode;
};

function DeleteVariableNode(
  props: NodeProps<DeleteVariableNodeData>
): React.ReactElement {
  const { data, isConnectable } = props;
  const { onNodeUpdate, onNodeDelete, intent } = useContext(BotBuilderContext);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  const deleteVariableNode = isDeleteVariableTreeNode(data.node.content)
    ? data.node.content
    : null;

  const [variableName, setVariableName] = useState<string>(
    deleteVariableNode?.variableName ?? ''
  );

  const availableVariables = useMemo(() => {
    const variables: Array<{ value: string; label: string }> = [];

    if (intent?.slots) {
      intent.slots.forEach((slot) => {
        variables.push({
          value: slot.slotName,
          label: slot.slotName
        });
      });
    }

    return variables;
  }, [intent?.slots]);

  const updateNodeData = useCallback(
    (updates: Partial<DeleteVariableTreeNode>) => {
      if (!deleteVariableNode) return;

      let hasChanges = false;
      Object.entries(updates).forEach(([key, value]) => {
        if (deleteVariableNode[key as keyof DeleteVariableTreeNode] !== value) {
          hasChanges = true;
        }
      });

      if (hasChanges) {
        Object.assign(deleteVariableNode, updates);
        onNodeUpdate(data.node);
      }
    },
    [deleteVariableNode, onNodeUpdate, data.node]
  );

  useEffect(() => {
    if (deleteVariableNode) {
      setVariableName(deleteVariableNode.variableName);
    }
  }, [deleteVariableNode]);

  useDebouncedEffect(
    () => {
      updateNodeData({ variableName });
    },
    [variableName, updateNodeData],
    300
  );

  const handleVariableNameChange = useCallback((value: string) => {
    setVariableName(value);
  }, []);

  const handleDelete = useCallback(async () => {
    onNodeDelete(data.node);
  }, [data.node, onNodeDelete]);

  return (
    <div style={{ position: 'relative' }}>
      <BotBuilderNode
        title='Delete variable'
        onNodeDelete={handleDelete}
        deleteButtonRef={deleteButtonRef}
        isLocked={data.isLocked}
      >
        <div>
          <CustomSelect
            placeholder='Select variable to delete...'
            value={variableName}
            onValueChange={handleVariableNameChange}
            options={availableVariables}
            className='bg-white'
            disabled={data.isLocked}
          />
          <div className='text-xs text-gray-500 mt-1'>
            Removes the variable from the context and database
          </div>
        </div>
      </BotBuilderNode>
      <Handle
        type='target'
        position={Position.Top}
        style={{ top: -8 }}
        isConnectable={isConnectable}
        id='target-handle'
      />
      <Handle
        type='source'
        position={Position.Bottom}
        style={{ bottom: -8 }}
        isConnectable={isConnectable}
        id='source-handle'
      />
    </div>
  );
}

export default DeleteVariableNode;
